import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl, apiError } from "../lib/api";
import { Check, Download, ChevronLeft, FileText, ArrowUpRight } from "lucide-react";

export default function Learn() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState([]);

  useEffect(() => {
    if (courseId === "residence-service-record") { window.location.replace("/tool/residence-service-record"); return; }
    api.get(`/my/courses/${courseId}`)
      .then((r) => {
        setCourse(r.data);
        setCompleted(r.data.completed_lessons || []);
        document.title = `${r.data.title} · Il Salotto Digitale`;
      })
      .catch((e) => setError(apiError(e)));
  }, [courseId]);

  const allLessons = useMemo(
    () => (course ? course.modules.flatMap((m) => m.lessons) : []),
    [course]
  );
  const total = allLessons.length;
  const pct = total ? Math.round((completed.length / total) * 100) : 0;

  const toggleComplete = async (lessonId, value) => {
    try {
      const { data } = await api.post("/my/progress", { course_id: courseId, lesson_id: lessonId, completed: value });
      setCompleted(data.completed_lessons);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return (
    <div className="min-h-screen pt-32 text-center px-6">
      <p className="font-serif text-3xl text-ink">{error}</p>
      <Link to="/account" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] link-underline">← Back to My Library</Link>
    </div>
  );
  if (!course) return <div className="min-h-screen pt-32 text-center text-sm text-ink-soft animate-pulse">Loading your library…</div>;

  // Product-specific presentation: Course 1 is a guide + reusable resource library,
  // not a linear lesson course, so we hide progress/completion for it only.
  const hideProgress = course.id === "how-to-start-as-an-estate-manager";
  const isFlagship = hideProgress;

  const Item = ({ lesson }) => {
    const res = lesson.resources?.[0];
    const isTool = (lesson.cta || "").toLowerCase().includes("download");
    const done = completed.includes(lesson.id);
    const href = res ? fileUrl(res.file_id) : (lesson.video_file_id ? fileUrl(lesson.video_file_id) : "");
    return (
      <div data-testid={`library-item-${lesson.id}`} className="group grid grid-cols-12 gap-4 lg:gap-8 items-center py-7 border-b border-line">
        <div className="col-span-12 lg:col-span-8 flex items-start gap-4">
          <span className="mt-1 text-accent shrink-0">{isTool ? <Download size={18} strokeWidth={1.5} /> : <FileText size={18} strokeWidth={1.5} />}</span>
          <div>
            <h3 className="font-serif font-light text-2xl lg:text-[1.7rem] text-ink leading-snug">{lesson.title}</h3>
            {lesson.subtitle && <p className="mt-1.5 text-sm text-ink-soft font-light">{lesson.subtitle}</p>}
            {lesson.content && (
              <div className="mt-3 text-sm text-ink-soft font-light leading-relaxed max-w-xl [&_a]:text-ink [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: lesson.content }} />
            )}
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 flex items-center lg:justify-end gap-5">
          {!hideProgress && (
            <button
              data-testid={`complete-${lesson.id}`}
              onClick={() => toggleComplete(lesson.id, !done)}
              title={done ? "Completed" : "Mark as done"}
              className={`w-6 h-6 border flex items-center justify-center shrink-0 transition-colors ${done ? "bg-ink border-ink text-bg" : "border-ink-soft/50 text-transparent hover:border-ink"}`}>
              <Check size={13} />
            </button>
          )}
          {href ? (
            <a
              data-testid={`open-${lesson.id}`}
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={() => { if (!hideProgress && !done) toggleComplete(lesson.id, true); }}
              className="inline-flex items-center gap-2 bg-ink text-bg px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors whitespace-nowrap">
              {lesson.cta || (isTool ? "Download" : "Open")}
              {isTool ? <Download size={14} /> : <ArrowUpRight size={14} />}
            </a>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="learn-page" className="min-h-screen pt-20 bg-bg">
      {/* Header */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-12 pt-12 lg:pt-16 pb-10">
          <Link to="/account" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline mb-8"><ChevronLeft size={14} /> My Library</Link>
          <p className="text-[11px] uppercase tracking-ultra text-accent">{course.tag}</p>
          <h1 className="mt-4 font-serif font-light text-4xl lg:text-6xl text-ink tracking-tight leading-[1.03]">{course.title}</h1>
          {course.subtitle && <p className="mt-5 max-w-2xl text-lg font-light text-ink-soft leading-relaxed">{course.subtitle}</p>}
          {!hideProgress && (
            <div className="mt-8 max-w-md">
              <div className="flex justify-between text-[11px] uppercase tracking-[0.15em] text-ink-soft mb-2"><span>Your progress</span><span>{pct}%</span></div>
              <div className="h-px bg-line relative"><div className="absolute left-0 top-0 h-px bg-ink transition-all duration-700" style={{ width: `${pct}%` }} /></div>
            </div>
          )}
        </div>
      </section>

      {/* BEGIN HERE — editorial orientation (flagship only) */}
      {isFlagship && (
        <section data-testid="course-orientation" className="border-b border-line">
          <div className="mx-auto max-w-[1100px] px-6 lg:px-12 py-16 lg:py-24">
            <p className="text-[11px] uppercase tracking-ultra text-accent">Begin Here</p>
            <p className="mt-6 max-w-3xl font-serif font-light text-2xl lg:text-[2rem] leading-[1.4] text-ink">
              This is not a course you need to complete in one sitting. Start with the guide. As you move through it, each professional tool and reference is introduced exactly when it becomes useful.
            </p>
            <p className="mt-8 text-[11px] uppercase tracking-[0.32em] text-ink-soft">
              Read <span className="text-accent mx-1">→</span> Observe <span className="text-accent mx-1">→</span> Build <span className="text-accent mx-1">→</span> Use
            </p>

            {/* Three parts of the experience */}
            <div className="mt-14 lg:mt-16 grid md:grid-cols-3 border-t border-line">
              {[
                { label: "The Guide", meta: "62-page editorial guide", body: "Understand the profession, the standard, how people get hired, what to establish before accepting a role, and your first thirty days." },
                { label: "The Tools", meta: "2 editable professional systems", body: "Build the Household Snapshot and Daily Log as your work develops." },
                { label: "The Practice", meta: "Workbook + quick references + carry card", body: "Develop your private-service awareness, communication and daily professional habits." },
              ].map((p) => (
                <div key={p.label} className="py-8 md:py-2 md:px-8 first:md:pl-0 last:md:pr-0 border-b md:border-b-0 md:border-r last:md:border-r-0 border-line">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-accent">{p.label}</p>
                  <p className="mt-3 font-serif font-light text-xl text-ink">{p.meta}</p>
                  <p className="mt-3 text-sm text-ink-soft font-light leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>

            {/* A look inside */}
            <div className="mt-16 lg:mt-20">
              <p className="text-[11px] uppercase tracking-ultra text-accent mb-6">A Look Inside</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
                {[
                  { src: "/course-preview/guide-cover.png", cap: "The Guide · Cover" },
                  { src: "/course-preview/guide-spread.png", cap: "Inside · The Standard" },
                  { src: "/course-preview/tool-snapshot.png", cap: "Professional Tool · Household Snapshot" },
                  { src: "/course-preview/tool-dailylog.png", cap: "Professional Tool · Daily Log" },
                ].map((im) => (
                  <figure key={im.src} data-testid="look-inside-item" className="group m-0">
                    <div className="overflow-hidden border border-line bg-white aspect-[3/4] shadow-[0_2px_10px_rgba(28,27,26,0.05)]">
                      <img src={im.src} alt={im.cap} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]" />
                    </div>
                    <figcaption className="mt-3 text-[10px] uppercase tracking-[0.16em] text-ink-soft leading-relaxed">{im.cap}</figcaption>
                  </figure>
                ))}
              </div>
              <p className="mt-6 text-xs font-light italic text-ink-soft">Editorial glimpses from the final materials.</p>
            </div>
          </div>
        </section>
      )}

      {/* Categorized library */}
      <section className="mx-auto max-w-[1100px] px-6 lg:px-12 py-14 lg:py-20">
        {course.modules.map((m, mi) => (
          <div key={m.id} data-testid={`category-${m.id}`} className={mi === 0 ? "" : "mt-16 lg:mt-20"}>
            <div className="flex items-baseline gap-4 mb-2">
              <span className="font-serif text-accent text-xl">{String(mi + 1).padStart(2, "0")}</span>
              <h2 className="text-[11px] uppercase tracking-ultra text-ink-soft">{m.title}</h2>
            </div>
            <div className="border-t border-line">
              {m.lessons.map((l) => <Item key={l.id} lesson={l} />)}
            </div>
          </div>
        ))}

        <div className="mt-16 flex items-center justify-between border-t border-line pt-8">
          <p className="text-sm font-light text-ink-soft">{total} {total === 1 ? "resource" : "resources"} in this product</p>
          <Link to="/account" data-testid="return-library" className="text-[11px] uppercase tracking-[0.2em] text-ink link-underline inline-flex items-center gap-2">
            <ChevronLeft size={14} /> Return to My Library
          </Link>
        </div>
      </section>
    </div>
  );
}
